import { useShallow } from 'zustand/react/shallow'
import { createWithEqualityFn } from 'zustand/traditional'
import { TrackChangesUpdateService } from '../TrackChangesUpdateService'

// Interface for the service contract
export interface ITrackChangesUpdateService {
    updateTrackChanges: () => Promise<void>
    cleanup?: () => void
}

// Store state interface with proper typing
interface TrackChangesUpdateStore {
    // Service instance
    service: TrackChangesUpdateService | null
    isTrackedChangesFetched: boolean

    // Actions
    setService: (service: TrackChangesUpdateService | null) => void
    setIsTrackedChangesFetched: (fetched: boolean) => void

    // Service method delegates
    updateTrackChanges: () => Promise<void>

    // Store management
    reset: () => void
}

/**
 * Store for managing track changes update service
 * Provides service management and state tracking
 */
export const useTrackChangesUpdateStore = createWithEqualityFn<
    TrackChangesUpdateStore
>()((set, get) => ({
    // State
    service: null,
    isTrackedChangesFetched: false,

    // Actions
    setService: (service: TrackChangesUpdateService | null) => {
        // Cleanup previous service if exists
        const prevService = get().service

        set({ service })
    },

    setIsTrackedChangesFetched: (fetched: boolean) => {
        set({ isTrackedChangesFetched: fetched })
    },

    // Service method delegates
    updateTrackChanges: async () => {
        const { service } = get()
        if (!service) {
            console.warn('TrackChangesUpdateService not initialized')
            return
        }

        try {
            await service.updateTrackChanges()
        } catch (error) {
            console.error('Store: Failed to update track changes:', error)
            throw error // Re-throw to allow caller to handle
        }
    },

    // Reset store to initial state
    reset: () => {
        const { service } = get()

        set({
            service: null,
            isTrackedChangesFetched: false,
        })
    },
}))

/**
 * Selector hook with shallow comparison
 */
export const useTrackChangesUpdateShallowSelector = <T>(
    selector: (state: TrackChangesUpdateStore) => T
): T => useTrackChangesUpdateStore(useShallow(selector))

/**
 * Convenience hook to get service availability status
 */
export const useTrackChangesServiceStatus = () =>
    useTrackChangesUpdateShallowSelector((state) => ({
        isServiceAvailable: !!state.service,
        isTrackedChangesFetched: state.isTrackedChangesFetched,
    }))

// Export the service interface for external use
export type { ITrackChangesUpdateService as TrackChangesUpdateServiceInterface }
